import React, { useRef, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import differenceInDays from 'date-fns/difference_in_days';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';

import ProductCover from 'Components/Media/Image/ProductCover';
import CardLabel from 'Components/Label/CardLabel';
import CardWishlistBtn from 'Components/Button/Card/CardWishlistBtn';
import HitCardPopupCriticScore from 'Components/Card/HitCardPopup/HitCardPopupCriticScore';
import StarRating from 'Components/Review/StarRating';
import Drm from 'Components/ProductInfo/Drm';
import DlcBadge from 'Components/Label/DlcBadge';
import AddToCartBtn from 'Components/Button/Cart/AddToCartCardBtn';
import CardViewBtn from 'Components/Button/Card/CardViewBtn';
import HitLinkTo from 'Helpers/Product/HitLinkTo';
import PriceContainer from 'Components/Price/PriceContainer';
import { toggleCheckoutModal } from 'Redux/ducks/modal';
import CountdownTimer from 'Components/Timer/CountdownTimer/CountdownTimer';
import { useHitCardPopup } from 'Context/HitCardPopupContext';
import HitCardPopupScreenshotCarousel from 'Components/Card/HitCardPopup/HitCardPopupScreenshotCarousel';
import HitCardPopupBundleGrid from 'Components/Card/HitCardPopup/HitCardPopupBundleGrid';
import HitCardPopupBundleTags from 'Components/Card/HitCardPopup/HitCardPopupBundleTags';

import 'Components/Card/HitCardPopup/HitCardPopup.scss';

const HitCardPopup = () => {
  const dispatch = useDispatch();
  const HitCardPopupRef = useRef();
  const authenticated = useSelector(state => state?.auth?.authenticated);
  const userLibrary = useSelector(state => state?.userLibrary);
  const steamLibrary = useSelector(state => state?.steamLibrary);
  const siteTheme = useSelector(state => state?.siteTheme);

  const { hit, position, hidePopup, cancelHide, config } = useHitCardPopup();
  const pathname = useSelector(state => state?.router?.location?.pathname);
  // used to determine if any modal is currently open
  const isModalOpen = useSelector(state => (
    Object.values(state?.modal || {}).some(value => value === true)
  ));
  const isOpen = !!(hit && position);

  // The popup is rendered at the app root, so it outlives the card that opened it and nothing
  // about that card can close it. It also can't rely on its own mouseleave: anything that reflows
  // the page while a popup is opening — devtools docking, a window resize — leaves it somewhere
  // the pointer never was, and a popup the pointer never entered never gets a mouseleave.
  useEffect(() => {
    if (!isOpen) return undefined;

    window.addEventListener('resize', hidePopup);
    return () => window.removeEventListener('resize', hidePopup);
  }, [isOpen, hidePopup]);

  useEffect(() => {
    hidePopup();
  }, [pathname]); // eslint-disable-line react-hooks/exhaustive-deps
  // stops the popup from being visible when any active modal is open
  useEffect(() => {
    if (isModalOpen) hidePopup();
  }, [isModalOpen, hidePopup]);

  if (!hit || !position) return null;

  const {
    isBigCard = false,
    isLg = false,
    productCoverSizes,
    adaptiveSize = false,
    coverType = '',
    flashDeal = false,
    showFlashDealBar = false,
    notAlgolia = false,
    hitCardLightTheme,
    hitCardDarkTheme,
    clickFunction = () => { },
    place,
    popupWidth,
    bundleCoverGrid = false,
  } = config || {};

  const {
    slug,
    name = '',
    type,
    categories = [],
    cover = '',
    portrait_cover: portraitCover = '',
    display_type: displayType = '',
    cover_alt_text: coverAltText = '',
    presale,
    features = [],
    free_to_play: freeToPlay = false,
    rating_score: rating,
    metacritic_score: metaCriticScore,
    is_srp_bundle: isSrpBundle = false,
    price,
    screenshots,
    bundle_covers: bundleCovers,
    game_total: gameTotal = 0,
    dlc_total: dlcTotal = 0,
  } = hit;

  // Pick-and-mix show their saving rather than their discount, as on HitCard
  const discountPercent = type === 'pick-and-mix' ? hit.pnm_saving : hit.discount_percent;

  const formattedBundleCovers = (bundleCovers || []).map(bundleCover => bundleCover.cover);

  let cardTheme = 'dark';
  if (hitCardLightTheme || siteTheme === 'light') cardTheme = 'light';
  if (hitCardDarkTheme) cardTheme = 'dark';
  const screenshotsToUse = screenshots?.length > 0 ? screenshots : formattedBundleCovers || [];

  const inLibrary = userLibrary.includes(hit.name);
  const inSteamLibrary = steamLibrary.includes(hit.name);
  const lowerCaseFeatures = features.map(feature => feature.toLowerCase());
  const isPromotion = type === 'pick-and-mix';
  const freeToPlayTag = lowerCaseFeatures.includes('free to play');

  let startingDate = hit.available_valid_from;
  let endingDate = hit.available_valid_until;

  let validDrm = '';
  let filteredDrm = [];
  if (hit && hit.drm && !flashDeal && !notAlgolia) filteredDrm = hit.drm.filter(d => d !== 'voucher' && d !== 'drm free');
  if (filteredDrm.length === 1) validDrm = filteredDrm[0];
  if (flashDeal || notAlgolia) {
    const validPlatforms = Object.keys(hit.drm).filter(d => hit.drm[d]);
    filteredDrm = validPlatforms.filter(platform => platform !== 'voucher');
    validDrm = filteredDrm[0];
    startingDate = hit.available_valid_from / 1000;
    endingDate = hit.available_valid_until / 1000;
  }

  let cardType = 'standard';
  if (hit.display_type === 'comic-bundle' || hit.display_type === 'comic') cardType = 'comic';
  if (hit.display_type === 'book-bundle' || hit.display_type === 'book') cardType = 'book';
  if (hit.display_type === 'elearning-bundle' || hit.display_type === 'elearning') cardType = 'elearning';
  if (hit.display_type === 'audio-bundle' || hit.display_type === 'audio') cardType = 'audio';
  if (hit.display_type === 'software-bundle' || hit.display_type === 'software') cardType = 'software';
  if (hit.display_type === 'graphic-novel') cardType = 'graphic-novel';
  const daysLeft = differenceInDays(new Date(endingDate * 1000), new Date());

  const showTimer = !isSrpBundle && (hit.type === 'bundle' || hit.type === 'pick-and-mix') && daysLeft < 90;

  const onLinkClick = () => {
    if (place === 'Mega Menu') {
      clickFunction();
    }

    if (place === 'Checkout Modal') {
      dispatch(toggleCheckoutModal());
    }

    hidePopup();
  };

  let showAddButton = false;
  if (!hit.tiered && !hit.presale && !hit.free_to_play && !hit.pay_what_you_want && hit.type !== 'pick-and-mix') {
    showAddButton = true;
  }


  const discountPercentOverride = hit.discount_percent_override;

  const onMouseLeave = () => {
    hidePopup();
  };

  let _labelOverride = config?.labelOverride;

  if (!_labelOverride) {
    if (inLibrary && authenticated && !hit.mystery) {
      _labelOverride = 'inLibrary';
    } else if (inSteamLibrary && authenticated && !hit.mystery) {
      _labelOverride = 'inSteamLibrary';
    }
  }

  return (
    <div
      ref={HitCardPopupRef}
      className={
        classNames('HitCardPopup  fadeInAnimation', {
          'HitCardPopup--isBigCard': isBigCard && !isLg,
          'HitCardPopup--isLg': isLg,
          'HitCardPopup--dark': cardTheme === 'dark',
          'HitCardPopup--light': cardTheme === 'light',
        })
      }
      style={{
        top: position.top,
        left: position.left,
        width: popupWidth ? `${popupWidth}px` : undefined,
      }}
      key={slug}
      onMouseEnter={cancelHide}
      onMouseLeave={onMouseLeave}
    >
      <CardLabel
        isPopup
        slug={hit.slug}
        bestDiscount={hit.best_ever}
        preorderDate={hit.release_date}
        startingDate={startingDate}
        endingDate={endingDate}
        type={hit.type}
        displayType={hit.display_type}
        presale={hit.presale}
        discountPercent={hit.discount_percent}
        flashDealCard={flashDeal || showFlashDealBar}
        noReleaseDate={hit.no_release_date}
        collections={hit.collections || []}
        systemDiscount={hit.system_discount}
        labelOverride={_labelOverride}
        earlyAccessDate={hit.preorder_playable_from}
        isStarDeal={hit.star_deal}
      />

      {bundleCoverGrid &&
        <HitCardPopupBundleGrid
          popupWidth={popupWidth}
          bundleCovers={bundleCovers}
          itemCount={gameTotal + dlcTotal}
          linkPath={HitLinkTo(hit)}
          linkFunc={onLinkClick}
        />
      }

      {!bundleCoverGrid && (!screenshotsToUse || screenshotsToUse.length === 0) &&
        <Link to={HitLinkTo(hit)} className="HitCardPopup__cover" onClick={onLinkClick}>
          <ProductCover
            lazy
            cover={cover}
            portraitCover={portraitCover}
            alt={coverAltText || `Product cover for ${name}`}
            sizesOverride={productCoverSizes}
            displayType={displayType}
            coverType={coverType}
            adaptiveSize={adaptiveSize}
          />
        </Link>
      }

      {!bundleCoverGrid && screenshotsToUse?.length > 0 &&
        <HitCardPopupScreenshotCarousel
          popupWidth={popupWidth}
          screenshots={screenshotsToUse.slice(0, 6)}
          displayType={displayType}
          type={type}
          linkPath={HitLinkTo(hit)}
          linkFunc={onLinkClick}
        />
      }

      <div className="HitCardPopup__content faux-block-link">
        <div className="HitCardPopup__content__titleButton">
          <Link to={HitLinkTo(hit)} className="HitCardPopup__title faux-block-link__overlay-link" onClick={onLinkClick}>
            {name}
          </Link>
          <div className="HitCardPopup__wishlistButtonContainer">
            {presale && (!freeToPlayTag || freeToPlay) && !isPromotion &&
              <CardWishlistBtn
                productId={hit.product_id}
                slug={hit.slug} hit={hit}

              />
            }
            {!presale && !isPromotion &&
              <CardWishlistBtn
                productId={hit.product_id}
                slug={hit.slug} hit={hit}
              />
            }

          </div>
        </div>

        <div className="HitCardPopup__content__reviews">
          {!!(rating && rating >= 4) &&
            <div className="HitCardPopup__content__stars">
              <StarRating rating={rating} />
            </div>
          }

          <HitCardPopupCriticScore
            metaCriticScore={metaCriticScore}
          />

        </div>

        {bundleCoverGrid &&
          <HitCardPopupBundleTags hit={hit} drm={filteredDrm[0]} />
        }

        {!bundleCoverGrid && categories?.length > 0 &&
          <div className="tag-column-container">
            {categories.slice(0, 3).map(tag =>
              <div className="tag-column" key={tag}>
                <div className="tag">
                  {tag}
                </div>
              </div>,
            )}
          </div>
        }

        <div className="hitCardStripe__content__icons HitCardPopUp__DrmPriceContainer">
          <div className="HitCardPopUp__DrmContainer">
            {(validDrm || cardType === 'standard') &&
              <React.Fragment>
                {validDrm &&
                  <Drm drmType={validDrm} colorOverride={cardTheme === 'dark' ? 'light' : 'dark'} />
                }
                {hit.display_type === 'dlc' &&
                  <DlcBadge />
                }
              </React.Fragment>
            }
          </div>

          <div className="hitCardStripe__content__purchase">
            {!showTimer &&
              <Link to={HitLinkTo(hit)} className="hitCardStripe__content__purchase__price faux-block-link__overlay-link" onClick={onLinkClick}>
                {hit.presale && (!freeToPlayTag || hit.free_to_play) &&
                  <div className="hitCardStripe__content__purchase__price__text">
                    <FormattedMessage id="hitcardpop.availablesoon" defaultMessage="Available Soon" />
                  </div>
                }

                {(hit.free_to_play || (hit.presale && freeToPlayTag)) &&
                  <div className="hitCardStripe__content__purchase__price__text">
                    <FormattedMessage id="hitcardpop.freetoplay" defaultMessage="Free to Play" />
                  </div>
                }

                {!hit.presale && !hit.free_to_play && !hit.hide_price &&
                  <PriceContainer
                    discountPercent={discountPercent}
                    discountPercentOverride={discountPercentOverride}
                    drm={validDrm}
                    fullPrice={hit.fullPrice}
                    giftButton={false}
                    hideDiscount={hit.hide_discount}
                    lightTheme={hitCardLightTheme}
                    mystery={hit.mystery}
                    payWhatYouWant={hit.pay_what_you_want}
                    price={price}
                    showAsterisk={!!hit.addVoucher}
                    slug={hit.slug}
                    tiered={hit.tiered}
                    type={hit.type}
                  />
                }
              </Link>
            }

            {showTimer &&
              <Link to={HitLinkTo(hit)} className="faux-block-link__overlay-link" onClick={onLinkClick}>
                <CountdownTimer
                  cardTimer
                  value={endingDate * 1000}
                />
              </Link>
            }

            {showAddButton &&
              <AddToCartBtn
                newIcon
                showInCartLink
                id={hit.product_id}
                slug={hit.slug}
                voucherToAdd={hit.addVoucher}
              />
            }

            {/* Bundles that can't be added straight to the cart (pick-and-mix, tiered) get a View button in its place */}
            {bundleCoverGrid && !showAddButton &&
              <div className="HitCardPopup__viewButton">
                <CardViewBtn
                  noFauxLink
                  hit={hit}
                  place={place}
                  clickFunction={clickFunction}
                  toggleModal={hidePopup}
                />
              </div>
            }
          </div>
        </div>
      </div>
    </div >
  );
};

export default HitCardPopup;
